import {
  DOCUMENT_FIELDS_PATH,
  type EntityFieldItemApplier,
  type EntityFieldItemOperation,
} from '@github-ui/optimistic-connection'
import {useConnectionMutationBridge} from '@github-ui/optimistic-connection/connectionMutationBridge'
import type {SettleTarget} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import type {SetFieldsMapper} from '@github-ui/optimistic-connection/pendingOperations'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'

import type {IssuesDashboardEnrichedDataPayload} from '../types/routes-types'

const INACTIVE_OWNER_ID = 'issues-dashboard-no-active-row'

/**
 * Dashboard rows are a denormalized payload that carries the issue's global node id as
 * `globalId` (`id` is the row's own database id). Producers publish only the global id, so
 * this document declares where it stores it — for the read-time overlay and for the
 * confirmed fold, which must match the same rows or confirmed data never lands.
 */
const ISSUE_ROW_IDENTITY_PATH = ['globalId']

type EnrichedRow = IssuesDashboardEnrichedDataPayload['issues'][number]
type IssueRow = Extract<EnrichedRow, {itemType: 'issue'}>
type IssueLabel = IssueRow['labels'][number]
type ProjectedLabel = Omit<IssueLabel, 'id'> & Required<Pick<IssueLabel, 'color' | 'nameHtml'>>
type ProjectedAssignee = Pick<IssueRow['assignees'][number], 'login' | 'avatarUrl'>

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isProjectedLabel(value: unknown): value is ProjectedLabel {
  return (
    isRecord(value) &&
    typeof value.name === 'string' &&
    typeof value.nameHtml === 'string' &&
    typeof value.color === 'string' &&
    (value.description === undefined || typeof value.description === 'string')
  )
}

function mapLabels(labels: unknown[]): ProjectedLabel[] | undefined {
  if (!labels.every(isProjectedLabel)) return undefined

  return labels.map(label => ({
    name: label.name,
    nameHtml: label.nameHtml,
    color: label.color,
    ...(label.description === undefined ? {} : {description: label.description}),
  }))
}

function isProjectedAssignee(value: unknown): value is ProjectedAssignee {
  return isRecord(value) && typeof value.login === 'string' && typeof value.avatarUrl === 'string'
}

function mapAssignedActors(assignedActors: unknown): ProjectedAssignee[] | undefined {
  if (!isRecord(assignedActors) || !Array.isArray(assignedActors.nodes)) return undefined
  if (!assignedActors.nodes.every(isProjectedAssignee)) return undefined

  return assignedActors.nodes.map(assignee => ({login: assignee.login, avatarUrl: assignee.avatarUrl}))
}

function mapIssueFieldValue(
  value: Readonly<Record<string, unknown>>,
): NonNullable<IssueRow['issueFieldValues']>[number] | undefined {
  const field = value.field
  if (!isRecord(field) || typeof field.name !== 'string') return undefined
  const fieldName = field.name

  switch (value.__typename) {
    case 'IssueFieldTextValue':
      return typeof value.value === 'string' || value.value === null
        ? {fieldName, dataType: 'text', value: value.value}
        : undefined
    case 'IssueFieldSingleSelectValue':
      return typeof value.name === 'string' || value.name === null
        ? {fieldName, dataType: 'single_select', value: value.name}
        : undefined
    case 'IssueFieldMultiSelectValue': {
      if (!Array.isArray(value.options)) return undefined
      const options = value.options.map(option => (isRecord(option) ? option.name : undefined))
      return options.every((name): name is string => typeof name === 'string')
        ? {fieldName, dataType: 'multi_select', options}
        : undefined
    }
    case 'IssueFieldDateValue':
      return typeof value.value === 'string' || value.value === null
        ? {fieldName, dataType: 'date', value: value.value}
        : undefined
    case 'IssueFieldNumberValue':
      return typeof value.numberValue === 'number' || value.numberValue === null
        ? {fieldName, dataType: 'number', numberValue: value.numberValue}
        : undefined
    default:
      return undefined
  }
}

function fieldNameFromOperation(operation: EntityFieldItemOperation): string | undefined {
  const field = operation.item.field
  return isRecord(field) && typeof field.name === 'string' ? field.name : undefined
}

export const applyIssueEntityFieldItemToIssueRow: EntityFieldItemApplier = (entity, operation) => {
  if (operation.field !== 'issueFieldValues') return
  const fieldName = fieldNameFromOperation(operation)
  if (!fieldName) return

  if (operation.type === 'removeEntityFieldItem') {
    if (!Array.isArray(entity.issueFieldValues)) return
    const values = entity.issueFieldValues as NonNullable<IssueRow['issueFieldValues']>
    const index = values.findIndex(value => value.fieldName === fieldName)
    if (index !== -1) values.splice(index, 1)
    return
  }

  const item = mapIssueFieldValue(operation.item)
  if (!item) return
  const values = Array.isArray(entity.issueFieldValues)
    ? (entity.issueFieldValues as NonNullable<IssueRow['issueFieldValues']>)
    : (entity.issueFieldValues = [])
  const index = values.findIndex(value => value.fieldName === fieldName)
  if (index === -1) values.push(item)
  else values[index] = item
}

export const mapIssueViewerFieldsToIssueRow: SetFieldsMapper = operationFields => {
  const fields: Record<string, unknown> = {}
  if (typeof operationFields.title === 'string') fields.title = operationFields.title
  if (typeof operationFields.titleHTML === 'string') fields.titleHtml = operationFields.titleHTML
  if (typeof operationFields.state === 'string') fields.state = operationFields.state
  if (operationFields.stateReason === null || typeof operationFields.stateReason === 'string') {
    fields.stateReason = operationFields.stateReason
  }
  if (typeof operationFields.closed === 'boolean') fields.closed = operationFields.closed
  if (operationFields.closedAt === null || typeof operationFields.closedAt === 'string') {
    fields.closedAt = operationFields.closedAt
  }
  const issueType = operationFields.issueType
  if (issueType === null) {
    fields.issueType = null
  } else if (
    issueType &&
    typeof issueType === 'object' &&
    !Array.isArray(issueType) &&
    typeof (issueType as Record<string, unknown>).name === 'string' &&
    typeof (issueType as Record<string, unknown>).color === 'string'
  ) {
    fields.issueType = {
      name: (issueType as Record<string, unknown>).name,
      color: (issueType as Record<string, unknown>).color,
    }
  }
  const milestone = operationFields.milestone
  if (milestone === null) {
    fields.milestone = null
  } else if (isRecord(milestone) && typeof milestone.title === 'string' && typeof milestone.url === 'string') {
    fields.milestone = {
      title: milestone.title,
      url: milestone.url,
    }
  }
  if (Array.isArray(operationFields.labels)) {
    const labels = mapLabels(operationFields.labels)
    if (labels) fields.labels = labels
  }
  const assignees = mapAssignedActors(operationFields.assignedActors)
  if (assignees) fields.assignees = assignees
  return Object.keys(fields).length > 0 ? fields : undefined
}

/**
 * Mirrors Relay-published issue field edits into the mutation cache. Settlement is not here: the
 * document that renders the rows names the caches it holds, via `useOptimisticDashboardRows`.
 */
export function useDashboardIssueMutationBridge({
  ownerId: requestedOwnerId,
  enabled,
}: {
  ownerId: string | undefined
  enabled: boolean
}): void {
  useConnectionMutationBridge({
    ownerId: requestedOwnerId ?? INACTIVE_OWNER_ID,
    connectionPath: DOCUMENT_FIELDS_PATH,
    enabled,
  })
}

export function useOptimisticDashboardRows({
  base,
  enabled,
  targets,
}: {
  base: EnrichedRow[]
  enabled: boolean
  /** The caches holding these rows: the list query, and the per-row node cache. */
  targets: SettleTarget[]
}): EnrichedRow[] {
  // No owner list: a `setFields` names its own target, so the overlay finds the rows this page
  // holds by scanning for their `globalId`. Enumerating owners here would have to be kept in step
  // with whatever the page happens to render, and silently misses a row when it falls behind.
  return useOptimisticData(base, {
    enabled,
    targets,
    identityPath: ISSUE_ROW_IDENTITY_PATH,
    mapFields: mapIssueViewerFieldsToIssueRow,
    applyEntityFieldItem: applyIssueEntityFieldItemToIssueRow,
  })
}
